import { useEffect, useMemo, useRef } from "react";
import { X } from "lucide-react";
import {
  LEGAL_COMERCIO_MODAL_TITLE,
  LEGAL_COMERCIO_SECTIONS,
  buildDatosIdentificativosBody,
  type LegalComercioSectionId,
  type LegalVendedorInfo,
} from "../../content/legalComercio";

interface LegalComercioModalProps {
  isOpen: boolean;
  onClose: () => void;
  focusSectionId?: LegalComercioSectionId;
  vendedorOverride?: LegalVendedorInfo | null;
}

export function LegalComercioModal({
  isOpen,
  onClose,
  focusSectionId,
  vendedorOverride = null,
}: LegalComercioModalProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const sections = useMemo(
    () =>
      LEGAL_COMERCIO_SECTIONS.map((section) =>
        section.id === "datos"
          ? { ...section, body: buildDatosIdentificativosBody(vendedorOverride) }
          : section
      ),
    [vendedorOverride]
  );

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !focusSectionId) return;
    const t = window.setTimeout(() => {
      const el = scrollRef.current?.querySelector(`#legal-section-${focusSectionId}`);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(t);
  }, [isOpen, focusSectionId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-black/55"
        onClick={onClose}
        aria-label="Cerrar"
      />
      <div
        className="relative z-10 flex max-h-[min(90vh,720px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-comercio-title"
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 sm:px-6">
          <h2
            id="legal-comercio-title"
            className="text-lg font-semibold text-[#2C2C2C] sm:text-xl"
          >
            {LEGAL_COMERCIO_MODAL_TITLE}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-[#2C2C2C]"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div ref={scrollRef} className="overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
          <div className="space-y-6 text-sm leading-relaxed text-gray-800">
            {sections.map((section) => (
              <section
                key={section.id}
                id={`legal-section-${section.id}`}
                className="scroll-mt-4"
              >
                <h3 className="mb-2 text-base font-semibold text-[#2C2C2C]">
                  {section.title}
                </h3>
                <p className="whitespace-pre-wrap">{section.body}</p>
              </section>
            ))}
          </div>
        </div>
        <div className="shrink-0 border-t border-gray-200 px-5 py-3 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg bg-[#1A3AB8] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#15308a] sm:w-auto"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
